import { db } from "../lib/firebase";
import { doc, setDoc, getDoc, collection, getDocs } from "firebase/firestore";

/**
 * One doc per day:
 *   Collection: schedules
 *   Doc ID: ISO date "YYYY-MM-DD"
 *
 * Data:
 *   {
 *     [roleLabel]: [employeeId, ...],              // legacy compatibility
 *     __assignments: {
 *       [roleLabel]: [
 *         { assignmentId, employeeId, start, end }
 *       ]
 *     },
 *     __times: {
 *       [roleLabel]: { start: "HH:MM", end: "HH:MM" }
 *     },
 *     __extraSlots: {
 *       [blockTitle]: [{ id, roleType, label, roleKey, insideParent, order }, ...]
 *     },
 *     __blockedSlots: {
 *       [roleLabel]: true
 *     },
 *     __unitText: {
 *       [roleLabel]: "Trinity 1"
 *     },
 *     __availableShifts: {
 *       [roleLabel]: [
 *         { id, roleKey, roleLabel, position, start, end, createdAt, createdBy }
 *       ]
 *     }
 *   }
 */
const SCHEDULES_COLL = "schedules";

/* ---------------- helpers ---------------- */
function isPlainObject(v) {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function cleanTimeObject(v) {
  if (!isPlainObject(v)) return { start: "", end: "" };
  return {
    start: typeof v.start === "string" ? v.start : "",
    end: typeof v.end === "string" ? v.end : "",
  };
}

function cleanAssignment(a) {
  if (!a) return null;

  if (typeof a === "string") {
    return {
      assignmentId: "",
      employeeId: a,
      start: "",
      end: "",
    };
  }

  if (!isPlainObject(a)) return null;

  const employeeId =
    typeof a.employeeId === "string"
      ? a.employeeId
      : typeof a.id === "string"
      ? a.id
      : typeof a.empId === "string"
      ? a.empId
      : "";

  return {
    assignmentId: typeof a.assignmentId === "string" ? a.assignmentId : "",
    employeeId,
    start: typeof a.start === "string" ? a.start : "",
    end: typeof a.end === "string" ? a.end : "",
  };
}

function cleanAvailableShift(s) {
  if (!isPlainObject(s)) return null;

  return {
    id: typeof s.id === "string" ? s.id : "",
    roleKey: typeof s.roleKey === "string" ? s.roleKey : "",
    roleLabel: typeof s.roleLabel === "string" ? s.roleLabel : "",
    position: typeof s.position === "string" ? s.position : "",
    start: typeof s.start === "string" ? s.start : "",
    end: typeof s.end === "string" ? s.end : "",
    createdAt: s.createdAt || null,
    createdBy: typeof s.createdBy === "string" ? s.createdBy : "",
  };
}

function normalizeDayData(raw) {
  const day = isPlainObject(raw) ? { ...raw } : {};

  if (!isPlainObject(day.__times)) day.__times = {};
  if (!isPlainObject(day.__extraSlots)) day.__extraSlots = {};
  if (!isPlainObject(day.__assignments)) day.__assignments = {};
  if (!isPlainObject(day.__blockedSlots)) day.__blockedSlots = {};
  if (!isPlainObject(day.__unitText)) day.__unitText = {};
  if (!isPlainObject(day.__availableShifts)) day.__availableShifts = {};

  const nextTimes = {};
  for (const [role, t] of Object.entries(day.__times)) {
    nextTimes[role] = cleanTimeObject(t);
  }
  day.__times = nextTimes;

  const nextExtra = {};
  for (const [blockTitle, arr] of Object.entries(day.__extraSlots)) {
    if (!Array.isArray(arr)) continue;

    nextExtra[blockTitle] = arr
      .filter((x) => isPlainObject(x) && typeof x.id === "string" && x.id.length > 0)
      .map((x) => ({
        id: x.id,
        roleType: typeof x.roleType === "string" ? x.roleType : "",
        label:
          typeof x.label === "string"
            ? x.label
            : typeof x.roleType === "string"
            ? x.roleType
            : "",
        roleKey: typeof x.roleKey === "string" ? x.roleKey : "",
        insideParent: !!x.insideParent,
        order:
          typeof x.order === "number" && Number.isFinite(x.order)
            ? x.order
            : undefined,
      }));
  }
  day.__extraSlots = nextExtra;

  const nextAssignments = {};
  for (const [role, arr] of Object.entries(day.__assignments)) {
    if (!Array.isArray(arr)) {
      nextAssignments[role] = [];
      continue;
    }

    nextAssignments[role] = arr
      .map(cleanAssignment)
      .filter((a) => a && a.employeeId);
  }
  day.__assignments = nextAssignments;

  const nextBlocked = {};
  for (const [role, val] of Object.entries(day.__blockedSlots)) {
    nextBlocked[role] = !!val;
  }
  day.__blockedSlots = nextBlocked;

  const nextUnitText = {};
  for (const [role, val] of Object.entries(day.__unitText)) {
    nextUnitText[role] = typeof val === "string" ? val : "";
  }
  day.__unitText = nextUnitText;

  const nextAvailable = {};
  for (const [role, arr] of Object.entries(day.__availableShifts)) {
    if (!Array.isArray(arr)) {
      nextAvailable[role] = [];
      continue;
    }

    nextAvailable[role] = arr
      .map(cleanAvailableShift)
      .filter((s) => s && s.id && s.roleKey && s.roleLabel && s.start && s.end);
  }
  day.__availableShifts = nextAvailable;

  for (const [k, v] of Object.entries(day)) {
    if (
      k === "__times" ||
      k === "__extraSlots" ||
      k === "__assignments" ||
      k === "__blockedSlots" ||
      k === "__unitText" ||
      k === "__availableShifts"
    ) {
      continue;
    }

    if (Array.isArray(v)) continue;
    day[k] = [];
  }

  return day;
}

function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value.map(stripUndefinedDeep).filter((v) => v !== undefined);
  }

  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const cleaned = stripUndefinedDeep(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out;
  }

  return value === undefined ? undefined : value;
}

/**
 * Save a single day (merge).
 * IMPORTANT: we merge so we don't clobber other keys.
 */
export async function saveDay(isoDate, dayData) {
  const ref = doc(db, SCHEDULES_COLL, isoDate);

  const cleaned = stripUndefinedDeep(normalizeDayData(dayData));

  console.log("[saveDay] writing doc:", `${SCHEDULES_COLL}/${isoDate}`, cleaned);
  try {
    await setDoc(ref, cleaned, { merge: true });
  } catch (err) {
    console.error("[saveDay] Firestore write failed:", err);
    throw err;
  }
}

/** Bulk read specific days */
export async function readDays(isoDates) {
  const result = {};
  for (const iso of isoDates) {
    const ref = doc(db, SCHEDULES_COLL, iso);
    try {
      const snap = await getDoc(ref);
      result[iso] = snap.exists() ? normalizeDayData(snap.data()) : normalizeDayData({});
    } catch (err) {
      console.error("[readDays] Firestore read failed:", `${SCHEDULES_COLL}/${iso}`, err);
      result[iso] = normalizeDayData({});
    }
  }
  console.log("[readDays] loaded:", result);
  return result;
}

/** Read ALL days in the schedules collection */
export async function readAllDays() {
  const result = {};
  try {
    const colRef = collection(db, SCHEDULES_COLL);
    const snap = await getDocs(colRef);
    snap.forEach((d) => {
      result[d.id] = d.exists() ? normalizeDayData(d.data()) : normalizeDayData({});
    });
  } catch (err) {
    console.error("[readAllDays] Firestore read failed:", err);
  }
  console.log("[readAllDays] loaded count:", Object.keys(result).length);
  return result;
}